import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  IconButton,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../../utils/api';
import { ListPageLayout } from '../../components/ListPage';
import { tokens } from '../../theme/tokens';

export default function HRPositions() {
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPosition, setEditingPosition] = useState(null);
  const [formData, setFormData] = useState({ name: '', active: true });

  useEffect(() => {
    fetchPositions();
  }, [page, rowsPerPage]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (page === 0) {
        fetchPositions();
      } else {
        setPage(0);
      }
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [search]);

  const fetchPositions = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/hr/positions', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search: search || undefined,
        },
      });
      setPositions(response.data.data || []);
      setTotal(Number(response.data.meta?.total) || 0);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch positions');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = (position = null) => {
    setEditingPosition(position);
    setFormData({ name: position?.name || '', active: position?.active !== undefined ? position.active === 1 : true });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPosition(null);
    setFormData({ name: '', active: true });
  };

  const handleSubmit = async () => {
    try {
      if (editingPosition) {
        await api.put(`/hr/positions/${editingPosition.id}`, formData);
      } else {
        await api.post('/hr/positions', formData);
      }
      handleCloseDialog();
      fetchPositions();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save position');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this position?')) {
      return;
    }
    try {
      await api.delete(`/hr/positions/${id}`);
      fetchPositions();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete position');
    }
  };

  return (
    <>
    <ListPageLayout
      title="Positions"
      crumbs={
        <>
          Registry / <Box component="b" sx={{ color: tokens.ink, fontWeight: 500 }}>Positions</Box>
        </>
      }
      searchPlaceholder="Search positions..."
      searchValue={search}
      onSearchChange={setSearch}
      error={error}
      onErrorClose={() => setError('')}
      actions={
        <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Position
          </Button>
      }
    >
      <TableContainer component={Paper} sx={{ overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {positions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                        No records yet
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  positions.map((position) => (
                    <TableRow key={position.id} hover>
                      <TableCell>{position.name}</TableCell>
                      <TableCell>
                        <Chip
                          label={position.active === 1 ? 'Active' : 'Inactive'}
                          color={position.active === 1 ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(position)}
                          title="Edit"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(position.id)}
                          title="Delete"
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </TableContainer>
    </ListPageLayout>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingPosition ? 'Edit Position' : 'Add Position'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            fullWidth
            variant="standard"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.active}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
              />
            }
            label="Active"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={!formData.name}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
